import { FC, ReactNode, createContext, useEffect, useReducer } from 'react';
import PropTypes from 'prop-types';
import { Amplify, Auth } from 'aws-amplify';
import { amplifyConfig2 } from '../config';
import { User } from 'src/models/user';
import { Shopper } from 'src/models/shopper';
import awsmobile from '../aws-exports';
import { API, graphqlOperation } from '@aws-amplify/api'
import { shopper, client, resortClient, linkedShopper, loginClientByOrderId } from '../graphql/queries'
import { ShopperQuery, ClientQuery, ResortClientQuery, LoginClientByOrderIdQuery, LinkedShopperQuery, LoginClientStatus } from '../API'
// import { useIntercom } from 'react-use-intercom';

Amplify.configure(awsmobile)
Amplify.configure(amplifyConfig2)

interface AuthState {
  isInitialized: boolean;
  isAuthenticated: boolean;
  user: User | null;
  shopper: Shopper | null;
}

interface AuthContextValue extends AuthState {
  method: 'Amplify';
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  register: (email: string, password: string) => Promise<void>;
  verifyCode: (username: string, code: string) => Promise<void>;
  resendCode: (username: string) => Promise<void>;
  passwordRecovery: (username: string) => Promise<void>;
  passwordReset: (
    username: string,
    code: string,
    newPassword: string
  ) => Promise<void>;
  updateShopper: (shopper: Shopper) => Promise<void>;
}

interface AuthProviderProps {
  children: ReactNode;
}

type InitializeAction = {
  type: 'INITIALIZE';
  payload: {
    isAuthenticated: boolean;
    user: User | null;
    shopper: Shopper | null;
  };
};

type LoginAction = {
  type: 'LOGIN';
  payload: {
    user: User;
    shopper: Shopper
  };
};

type LogoutAction = {
  type: 'LOGOUT';
};

type RegisterAction = {
  type: 'REGISTER';
};

type VerifyCodeAction = {
  type: 'VERIFY_CODE';
};

type ResendCodeAction = {
  type: 'RESEND_CODE';
};
type PasswordRecoveryAction = {
  type: 'PASSWORD_RECOVERY';
};

type PasswordResetAction = {
  type: 'PASSWORD_RESET';
};

type UpdateShopperAction = {
  type: 'UPDATE_SHOPPER';
  payload: {
    shopper: Shopper
  }
};

type Action =
  | InitializeAction
  | LoginAction
  | LogoutAction
  | RegisterAction
  | VerifyCodeAction
  | ResendCodeAction
  | PasswordRecoveryAction
  | PasswordResetAction
  | UpdateShopperAction;

const initialAuthState: AuthState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
  shopper: null
};

const handlers: Record<
  string,
  (state: AuthState, action: Action) => AuthState
> = {
  INITIALIZE: (state: AuthState, action: InitializeAction): AuthState => {
    const { isAuthenticated, user, shopper } = action.payload;

    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
      shopper
    };
  },
  LOGIN: (state: AuthState, action: LoginAction): AuthState => {
    const { user, shopper } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user,
      shopper
    };
  },
  LOGOUT: (state: AuthState): AuthState => ({
    ...state,
    isAuthenticated: false,
    user: null,
    shopper: null
  }),
  REGISTER: (state: AuthState): AuthState => ({ ...state }),
  VERIFY_CODE: (state: AuthState): AuthState => ({ ...state }),
  RESEND_CODE: (state: AuthState): AuthState => ({ ...state }),
  PASSWORD_RECOVERY: (state: AuthState): AuthState => ({ ...state }),
  PASSWORD_RESET: (state: AuthState): AuthState => ({ ...state }),
  UPDATE_SHOPPER: (state: AuthState, action: UpdateShopperAction): AuthState => {
    const {shopper } = action.payload;

    return {
      ...state,
      shopper
    };
  },
};

const reducer = (state: AuthState, action: Action): AuthState =>
  handlers[action.type] ? handlers[action.type](state, action) : state;

const AuthContext = createContext<AuthContextValue>({
  ...initialAuthState,
  method: 'Amplify',
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  register: () => Promise.resolve(),
  verifyCode: () => Promise.resolve(),
  resendCode: () => Promise.resolve(),
  passwordRecovery: () => Promise.resolve(),
  passwordReset: () => Promise.resolve(),
  updateShopper: () => Promise.resolve()
});

export const AuthProvider: FC<AuthProviderProps> = (props) => {
  const { children } = props;
  const [state, dispatch] = useReducer(reducer, initialAuthState);
  // const { boot, shutdown, hide, show, update } = useIntercom();

  useEffect(() => {
    const initialize = async (): Promise<void> => {
      console.log("INIT")
      try {
        const cognintoUser = await Auth.currentAuthenticatedUser();
        const clientUser = await API.graphql({...graphqlOperation(resortClient, {id: cognintoUser.username}), authMode: "AMAZON_COGNITO_USER_POOLS"}) as {data: ResortClientQuery};
        const clientShopper =  await API.graphql({...graphqlOperation(linkedShopper, {id: cognintoUser.username}), authMode: "AMAZON_COGNITO_USER_POOLS"}) as {data: LinkedShopperQuery};
        
        // boot({ userId: clientUser.data.resortClient.id, hideDefaultLauncher: true });
        
        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: true,
            user: {
              id: clientUser.data.resortClient.id,
              // jobtitle: 'Job',
              avatar: clientUser.data.resortClient.image,
              //email: user.email,
              name: clientUser.data.resortClient.firstName + ' ' + clientUser.data.resortClient.lastName,
              displayName: clientUser.data.resortClient.firstName,
              // role: user.role,
              // location: user.location,
              // username: user.username,
              // posts: user.posts,
              // coverImg: user.coverImg,
              // followers: user.followers,
              // description: user.description
            },
            shopper: {
              id: clientShopper.data.linkedShopper.id,
              jobtitle: 'Job',
              avatar: '', //clientShopper.data.linkedShopper.shopper.image, // we are setting this when the order loads as it can be different for referral orders
              email: clientShopper.data.linkedShopper.shopper.email,
              name: clientShopper.data.linkedShopper.shopper.displayName,
              businessName: '', //clientShopper.data.linkedShopper.shopper.businessName, // we are setting this when the order loads as it can be different for referral orders
            }
          }
        });
      } catch (error) {
        console.error(error)
        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: false,
            user: null,
            shopper: null
          }
        });
      }
    };

    initialize();
  }, []);

  const login = async (email: string, password: string): Promise<void> => {
    console.log("LOGIN")
    // Make sure the email is lower case, VERY IMPORTANT
    email = email.toLocaleLowerCase()
    // Load the challenges into the cogntio attributes by makie unauthorized api key call
    const loginStatus = await API.graphql({...graphqlOperation(loginClientByOrderId, {id: email}), authMode: "API_KEY"}) as {data: LoginClientByOrderIdQuery};
    console.log("login status", loginStatus)
    // Need to test this for bad phones
    try {
      const user = await Auth.signIn(email);
      console.log("user, ", user)

      // This is the custom auth flow
      if (user?.challengeName === "CUSTOM_CHALLENGE") {
        // Custom challenge flow
        try {
          // Send answer to the cutsom challenge
          const challengeResult = await Auth.sendCustomChallengeAnswer(user, password)
          console.log("challengeResult, ", challengeResult)
        } catch (err) {
          console.error(err)
          if (err.code === 'NotAuthorizedException') {
            // This happens when the code is incorect, change the message so it is not confusing
            err.code = 'NotAuthorizedCodeException'
            const isBillingZipCredential = /^\d{5}(-\d{4})?$/.test(password.trim())
            err.message = isBillingZipCredential
              ? 'The billing ZIP code does not match the card on file for this email.'
              : 'Please enter a valid order id for the provided email. You can check your order confirmation email for the correct order ID.'
          }
          throw err;
        }
      }

      try {
        const clientUser = await API.graphql({...graphqlOperation(resortClient, {id: user.username}), authMode: "AMAZON_COGNITO_USER_POOLS"}) as {data: ResortClientQuery};
        console.log("clientUser, ", clientUser)
        const clientShopper =  await API.graphql({...graphqlOperation(linkedShopper, {id: user.username}), authMode: "AMAZON_COGNITO_USER_POOLS"}) as {data: LinkedShopperQuery};
  
        // // Crude check that someone is a resort user
        // if (!shopperUser.data.shopper.subscriptionPlan.includes('Resort') && !allowedCoordinatorShopperIds.includes(shopperUser.data.shopper.id) ) {
        //   const error = new Error('Your account does not have permission to use this tool.'); 
        //   // error.code = 'UserNoPermission'
        //   throw error
        // }
    
        // boot({ userId: clientUser.data.resortClient.id, hideDefaultLauncher: true });
  
        dispatch({
          type: 'LOGIN',
          payload: {
            // TODO - do we need to send isAuthenticated ??
            user: {
              id: clientUser.data.resortClient.id,
              // jobtitle: "Job",
              avatar: clientUser.data.resortClient.image,
              //email: user.email,
              name: clientUser.data.resortClient.firstName + ' ' + clientUser.data.resortClient.lastName,
              displayName: clientUser.data.resortClient.firstName,
              // role: user.role,
              // location: user.location,
              // username: user.username,
              // posts: user.posts,
              // coverImg: user.coverImg,
              // followers: user.followers,
              // description: user.description
            },
            shopper: {
              id: clientShopper.data.linkedShopper.id,
              jobtitle: 'Job',
              avatar: '', //clientShopper.data.linkedShopper.shopper.image, // we are setting this when the order loads as it can be different for referral orders
              email: clientShopper.data.linkedShopper.shopper.email,
              name: clientShopper.data.linkedShopper.shopper.displayName,
              businessName: '', //clientShopper.data.linkedShopper.shopper.businessName, // we are setting this when the order loads as it can be different for referral orders
            }
          }
        });
      } catch(err) {
        console.error(err)
        // Crude check for catching the error we created above
        // TODO - Make this more robust, maybe using Error.cause
        if (err.message == 'Your account does not have permission to use this tool.') {
          throw err
        }
        const error = new Error(err.errors[0].message);  // This will display any api access errors imposed on the backend and stop login
        // error.code = 'UserNoPermission'
        throw error
        
      }
    } catch (err) {
      console.error(err)
      if (err.code === 'NotAuthorizedException') {
        // This happens when we spot a home user tring to login
        // TODO - it also happens when a resort user has no orders!
        err.code = 'NotAuthorizedClientException'
        err.message = 'This website only works with Resort Grocery orders. Please use the Dumpling app to manage your orders.'
      }
      throw err
    }

  



  };

  const logout = async (): Promise<void> => {
    await Auth.signOut();
    dispatch({
      type: 'LOGOUT'
    });
  };

  const register = async (email: string, password: string): Promise<void> => {
    await Auth.signUp({
      username: email,
      password,
      attributes: { email }
    });
    dispatch({
      type: 'REGISTER'
    });
  };

  const verifyCode = async (username: string, code: string): Promise<void> => {
    await Auth.confirmSignUp(username, code);
    dispatch({
      type: 'VERIFY_CODE'
    });
  };

  const resendCode = async (username: string): Promise<void> => {
    await Auth.resendSignUp(username);
    dispatch({
      type: 'RESEND_CODE'
    });
  };

  const passwordRecovery = async (username: string): Promise<void> => {
    await Auth.forgotPassword(username);
    dispatch({
      type: 'PASSWORD_RECOVERY'
    });
  };

  const passwordReset = async (
    username: string,
    code: string,
    newPassword: string
  ): Promise<void> => {
    await Auth.forgotPasswordSubmit(username, code, newPassword);
    dispatch({
      type: 'PASSWORD_RESET'
    });
  };

  const updateShopper = async (shopper: Shopper): Promise<void> => {
    dispatch({
      type: 'UPDATE_SHOPPER',
      payload: {
        shopper: shopper
      }
    });
  }

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'Amplify',
        login,
        logout,
        register,
        verifyCode,
        resendCode,
        passwordRecovery,
        passwordReset,
        updateShopper
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export default AuthContext;
